/**
 * GlobalView — Vessel Icons by Ship Type
 *
 * SVG icons for vessel categories rendered as Canvas and Data URI.
 * Each ship type gets a distinct icon shape and default color.
 * Icons are oriented pointing upward (north / 0° heading).
 *
 * Categories: Cargo, Tanker, Passenger, Fishing, High-speed, Special, Unknown.
 */

// ─── Category Labels & Colors ────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  Cargo: '#4a9aff',
  Tanker: '#ff6b4a',
  Passenger: '#4aff9a',
  Fishing: '#ffc94a',
  'High-speed': '#ff4adc',
  Special: '#9a4aff',
  Unknown: '#888888',
};

export function getVesselCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? CATEGORY_COLORS.Unknown;
}

export function getVesselCategoryLabel(category: string): string {
  return category || 'Unknown';
}

// ─── SVG Path Data (viewBox 0 0 24 24) ──────────────────────────

/** Ship hull shape — common to all types */
const HULL_PATH = 'M12 2 L8 6 L6 10 L5 18 L7 22 L17 22 L19 18 L18 10 L16 6 Z';

/** Cargo: hull with container stack */
const CARGO_EXTRA = 'M8 10 H16 V16 H8 Z M12 10 V16 M8 13 H16';

/** Tanker: hull with dome/tank shape */
const TANKER_EXTRA = 'M8 10 Q8 8 12 8 Q16 8 16 10 L16 16 Q16 18 12 18 Q8 18 8 16 Z';

/** Passenger: hull with deck lines */
const PASSENGER_EXTRA = 'M8 10 H16 M8 13 H16 M8 16 H16 M10 8 H14 V10';

/** Fishing: hull with crane/boom */
const FISHING_EXTRA = 'M12 6 L18 3 M12 6 L12 10 M9 12 H15';

/** High-speed: sleek hull */
const HIGHSPEED_PATH = 'M12 1 L9 5 L7 10 L6 18 L8 22 L16 22 L18 18 L17 10 L15 5 Z';

/** Special: diamond marker */
const SPECIAL_PATH = 'M12 3 L19 12 L12 21 L5 12 Z';

// ─── Canvas Rendering ────────────────────────────────────────────

const CANVAS_SIZE = 48;
const SVG_VIEWBOX = 24;
const canvasCache = new Map<string, HTMLCanvasElement>();

function setupCtx(ctx: CanvasRenderingContext2D, fill: string): void {
  const scale = CANVAS_SIZE / SVG_VIEWBOX;
  ctx.scale(scale, scale);
  ctx.fillStyle = fill;
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  ctx.lineWidth = 0.8;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
}

function drawPath(ctx: CanvasRenderingContext2D, pathData: string): void {
  const path = new Path2D(pathData);
  ctx.fill(path);
  ctx.stroke(path);
}

function drawStrokePath(ctx: CanvasRenderingContext2D, pathData: string): void {
  const path = new Path2D(pathData);
  ctx.stroke(path);
}

function drawCargo(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  drawPath(ctx, HULL_PATH);
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 0.6;
  drawStrokePath(ctx, CARGO_EXTRA);
}

function drawTanker(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  drawPath(ctx, HULL_PATH);
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  drawPath(ctx, TANKER_EXTRA);
}

function drawPassenger(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  drawPath(ctx, HULL_PATH);
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 0.6;
  drawStrokePath(ctx, PASSENGER_EXTRA);
}

function drawFishing(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  drawPath(ctx, HULL_PATH);
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 0.8;
  drawStrokePath(ctx, FISHING_EXTRA);
}

function drawHighSpeed(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  drawPath(ctx, HIGHSPEED_PATH);
}

function drawSpecial(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  drawPath(ctx, SPECIAL_PATH);
  // 'S' marker
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.font = 'bold 8px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('S', 12, 12);
}

function drawUnknown(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  drawPath(ctx, HULL_PATH);
}

const DRAW_FUNCTIONS: Record<string, (ctx: CanvasRenderingContext2D, fill: string) => void> = {
  Cargo: drawCargo,
  Tanker: drawTanker,
  Passenger: drawPassenger,
  Fishing: drawFishing,
  'High-speed': drawHighSpeed,
  Special: drawSpecial,
  Unknown: drawUnknown,
};

/**
 * Get a Canvas element with the vessel icon drawn on it.
 * Results are cached. Suitable for Cesium BillboardCollection.
 */
export function getVesselIconCanvas(category: string, fill?: string): HTMLCanvasElement {
  const color = fill ?? getVesselCategoryColor(category);
  const key = `${category}:${color}`;
  const cached = canvasCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const drawFn = DRAW_FUNCTIONS[category] ?? drawUnknown;
  drawFn(ctx, color);

  canvasCache.set(key, canvas);
  return canvas;
}

// ─── Data URI (SVG) ──────────────────────────────────────────────

function makeSvg(innerSvg: string, fill: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32"><g fill="${fill}" stroke="rgba(0,0,0,0.6)" stroke-width="0.8" stroke-linejoin="round" stroke-linecap="round">${innerSvg}</g></svg>`;
}

const SVG_INNER: Record<string, () => string> = {
  Cargo: () => `<path d="${HULL_PATH}"/><path d="${CARGO_EXTRA}" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="0.6"/>`,
  Tanker: () => `<path d="${HULL_PATH}"/><path d="${TANKER_EXTRA}" fill="rgba(255,255,255,0.2)"/>`,
  Passenger: () => `<path d="${HULL_PATH}"/><path d="${PASSENGER_EXTRA}" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="0.6"/>`,
  Fishing: () => `<path d="${HULL_PATH}"/><path d="${FISHING_EXTRA}" fill="none" stroke="rgba(255,255,255,0.6)"/>`,
  'High-speed': () => `<path d="${HIGHSPEED_PATH}"/>`,
  Special: () => `<path d="${SPECIAL_PATH}"/><text x="12" y="14" font-size="8" text-anchor="middle" font-weight="bold" fill="rgba(0,0,0,0.6)" stroke="none">S</text>`,
  Unknown: () => `<path d="${HULL_PATH}"/>`,
};

const dataUriCache = new Map<string, string>();

/** Get SVG data URI for billboards */
export function getVesselIconDataUri(category: string, fill?: string): string {
  const color = fill ?? getVesselCategoryColor(category);
  const key = `${category}:${color}`;
  const cached = dataUriCache.get(key);
  if (cached) return cached;

  const innerFn = SVG_INNER[category] ?? SVG_INNER.Unknown;
  const svg = makeSvg(innerFn(), color);
  const uri = `data:image/svg+xml;base64,${btoa(svg)}`;

  dataUriCache.set(key, uri);
  return uri;
}
